import { InclusionMethod } from "@/generated/company-search";
import {
  GlobalPresenceOption,
  LocationTypeOption,
} from "@/components/filters/UserFilters/GlobalPresenceFilter/constants";
import type { CompanyFilters } from "../../types";
import { defaultGlobalPresenceSlice, type GlobalPresenceSlice } from "./slice";

type GlobalPresenceValue = NonNullable<CompanyFilters["globalPresence"]>;
type SubsidiaryValue = NonNullable<GlobalPresenceValue["subsidiaryLocations"]>;

export function toCompanyFilters(
  slice: GlobalPresenceSlice
): Partial<CompanyFilters> {
  if (slice.option === GlobalPresenceOption.MULTI_NATIONAL) {
    return { globalPresence: { isMultiNational: true } };
  }

  if (slice.option === GlobalPresenceOption.BY_LOCATION) {
    const globalPresence: GlobalPresenceValue = { isMultiNational: false };

    if (slice.hqValues.length > 0) {
      if (slice.hqType === LocationTypeOption.COUNTRY) {
        globalPresence.hqCountryCodes = slice.hqValues;
      } else {
        globalPresence.hqRegions = slice.hqValues;
      }
    }

    if (slice.subValues.length > 0) {
      const subsidiaryLocations: SubsidiaryValue = {
        inclusionMethod:
          slice.subMatch === "ALL"
            ? InclusionMethod.ALL_PRESENT
            : InclusionMethod.ANY_PRESENT,
      };

      if (slice.subType === LocationTypeOption.COUNTRY) {
        subsidiaryLocations.countryCodes = slice.subValues;
      } else {
        subsidiaryLocations.regions = slice.subValues;
      }

      if (slice.subMatch !== "ANY" && slice.subMatch !== "ALL") {
        const parsed = parseInt(slice.subMatch, 10);
        if (!Number.isNaN(parsed)) subsidiaryLocations.countMatching = parsed;
      }

      globalPresence.subsidiaryLocations = subsidiaryLocations;
    }

    return { globalPresence };
  }

  // GlobalPresenceOption.NONE — contribute nothing.
  return {};
}

export function fromCompanyFilters(
  filters: CompanyFilters
): GlobalPresenceSlice {
  const gp = filters.globalPresence;
  if (!gp) return defaultGlobalPresenceSlice;

  if (gp.isMultiNational === true) {
    return {
      ...defaultGlobalPresenceSlice,
      option: GlobalPresenceOption.MULTI_NATIONAL,
    };
  }

  const sub = gp.subsidiaryLocations;
  const hasLocation =
    !!gp.hqCountryCodes?.length || !!gp.hqRegions?.length || !!sub;

  if (gp.isMultiNational === false && hasLocation) {
    const notNull = (v: string | null): v is string => v != null;
    const hqValues = (gp.hqCountryCodes ?? gp.hqRegions ?? []).filter(notNull);
    const subValues = (sub?.countryCodes ?? sub?.regions ?? []).filter(notNull);

    const subMatch =
      sub?.inclusionMethod === InclusionMethod.ALL_PRESENT
        ? "ALL"
        : sub?.countMatching
          ? String(sub.countMatching)
          : "ANY";

    return {
      option: GlobalPresenceOption.BY_LOCATION,
      showHq: !!gp.hqCountryCodes?.length || !!gp.hqRegions?.length,
      showSub: !!sub,
      hqType: gp.hqRegions?.length
        ? LocationTypeOption.REGION
        : LocationTypeOption.COUNTRY,
      hqValues,
      subType: sub?.regions?.length
        ? LocationTypeOption.REGION
        : LocationTypeOption.COUNTRY,
      subValues,
      subMatch,
    };
  }

  return defaultGlobalPresenceSlice;
}
